import type { MapFeature } from "../redux/global-map.types"

/**
 * The two map-context summaries ("Projects by Status", "Top Locations") are
 * derived from the SAME `/features` response the map itself already renders —
 * no extra request, no new endpoint, no backend work. They therefore describe
 * exactly the records currently on the map, which is what makes them map
 * context rather than a second copy of the Analytics tab.
 *
 * A clustered response (`mode: "clusters"`, 300+ matches) enumerates no
 * individual features, so both summaries are empty there by construction; the
 * caller renders its own "too many to summarise" state rather than a
 * misleading partial count.
 */

export interface SummaryRow {
  key: string
  label: string
  count: number
}

/** Descending by count, then alphabetical so equal counts have a stable order. */
function rank(rows: Map<string, SummaryRow>, limit?: number): SummaryRow[] {
  const sorted = [...rows.values()].sort(
    (a, b) => b.count - a.count || a.label.localeCompare(b.label)
  )
  return limit ? sorted.slice(0, limit) : sorted
}

/**
 * Counts the on-map records by their own `status_label` — the same string the
 * status pill and the List tab already show, so the card can never disagree
 * with the rows it summarises.
 */
export function summarizeByStatus(features: MapFeature[]): SummaryRow[] {
  const rows = new Map<string, SummaryRow>()
  for (const feature of features) {
    const label = feature.status_label?.trim()
    if (!label) continue
    const existing = rows.get(label)
    if (existing) existing.count += 1
    else rows.set(label, { key: label, label, count: 1 })
  }
  return rank(rows)
}

// A trailing `CA` or `CA 92109` is the state (and ZIP), never the city. Matched
// so the city can be read as "the segment before the state" from both of the
// shapes the backend sends: the full `"1876 Ocean Front Walk, San Diego, CA
// 92109"` for a caller with exact-location rights, and the redacted
// `"San Diego, CA"` for one without.
const STATE_SEGMENT = /^[A-Za-z]{2}(\s+\d{5}(-\d{4})?)?$/

// Google's formatted addresses end with the country ("…, CA 94025, USA"),
// which is useless here: a contractor's records are essentially all in one
// country, so grouping by it produced a single "USA" row. Dropped before the
// state so the city is what is left.
const COUNTRY_SEGMENT = /^(usa|u\.s\.a\.|us|united states( of america)?)$/i

/**
 * The city of a one-line address, or `null` when the string has no segment
 * that can be read as one. "Top Locations" is a local operational summary —
 * which cities the work is in — so a country segment is peeled off and never
 * becomes a row. Deliberately conservative: a record whose address
 * cannot be parsed is left out of the summary rather than bucketed under a
 * guess, because a wrong city is worse than a missing row.
 */
export function cityOf(address: string | null): string | null {
  if (!address) return null
  const all = address
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean)
  // Peel the country off the end first, then the state/ZIP; whatever is last
  // after that is the city.
  const parts = all.length > 1 && COUNTRY_SEGMENT.test(all[all.length - 1])
    ? all.slice(0, -1)
    : all
  if (parts.length === 0) return null
  // Drop a trailing state/ZIP segment if there is one; whatever is last then
  // is the city. A single-segment address is a street or a place name, not a
  // city we can trust.
  const withoutState = STATE_SEGMENT.test(parts[parts.length - 1])
    ? parts.slice(0, -1)
    : parts
  if (withoutState.length < 2 && withoutState.length === all.length) {
    return null
  }
  const city = withoutState[withoutState.length - 1]
  return city && !STATE_SEGMENT.test(city) ? city : null
}

/**
 * Counts the on-map records by city. `limit` caps the card at its top rows —
 * the remainder is simply not shown, never rolled into an "Other" bucket that
 * would imply a total the card is not reporting.
 */
export function summarizeByLocation(
  features: MapFeature[],
  limit = 5
): SummaryRow[] {
  const rows = new Map<string, SummaryRow>()
  for (const feature of features) {
    const city = cityOf(feature.address)
    if (!city) continue
    const key = city.toLowerCase()
    const existing = rows.get(key)
    if (existing) existing.count += 1
    else rows.set(key, { key, label: city, count: 1 })
  }
  return rank(rows, limit)
}
